<template>
<Teleport to="body">
  <div class="fixed inset-0 z-50 flex justify-center bg-black/60">
    <div class="m-5 w-full md:w-8/12 lg:w-4/12 xl:w-3/12" @click.self="closeDialog(false)" >

      <div class="p-0 bg-gray-50 shadow-xl border border-gray-200 rounded-md text-center">
        
        <div class="flex flex-col w-full text-black">
        
          <BlockUI :blocked="blocked">

            <div v-if="blocked" class="absolute inset-0 flex items-center justify-center z-50 bg-black/20">
              <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="5" fill="transparent" animationDuration=".5s" />
            </div>

            <div class="flex flex-col w-full rounded-t-md p-3 bg-gradient-to-r bg-[#243665]">
              <div>
                <span class="pi pi-lock w-6 h-6 text-white" />
              </div>
              <div class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis"> 
                {{ $t('titulo.cambiar.password') }}
              </div>
            </div>

            <Form :validation-schema="schema" @invalid-submit="onInvalidSubmit">

              <div class="container p-4 text-left space-y-3">

                <div class="w-full text-center" v-show="errorCambioClave">
                  <div class="bg-red-100 text-sm border border-red-300 text-red-700 px-4 py-3 rounded relative">
                    <span class="block sm:inline">{{errorCambioClave}}</span>
                  </div>
                </div>

                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.clave.actual') }}</label>
                    <Field name="claveActual" v-slot="{ field, errors }" v-model="claveActual">
                      <InputText v-bind="field" type="password" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="claveActual" /></span>
                    </Field>
                  </div>
                </div>
                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.clave.nueva') }}</label>
                    <Field name="claveNueva" v-slot="{ field, errors }" v-model="claveNueva">
                      <InputText v-bind="field" type="password" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="claveNueva" /></span>
                    </Field>
                  </div>
                </div>
                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.clave.nueva.repetir') }}</label>
                    <Field name="claveRepetida" v-slot="{ field, errors }" v-model="claveRepetida">
                      <InputText v-bind="field" type="password" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="claveRepetida" /></span>
                    </Field>
                  </div>
                </div>

              </div>

            </Form>

            <div class="p-3 flex flex-col md:flex-row gap-2 justify-end w-full mt-2 bg-gray-100">
              <a @click="closeDialog(false)" class="cursor-pointer px-4 py-2 bg-transparent border-red-600 hover:bg-red-600 text-red-600 font-semibold hover:text-white border rounded-xl">
                <font-awesome-icon :icon="['fas', 'fa-xmark']" /> {{ $t('boton.cancelar') }}
              </a>
              <a @click="aplicarCambios" class="cursor-pointer px-4 py-2 bg-transparent border-[#243665] hover:bg-[#243665] text-[#243665] font-semibold hover:text-white border rounded-xl">
                <font-awesome-icon :icon="['fas', 'fa-check-double']" /> {{ $t('boton.aceptar') }}
              </a>
            </div>
          </BlockUI>


        </div>
      </div>

    </div>
  </div>
</Teleport>  

</template>

<script lang="ts">
import * as yup from 'yup';

import { defineComponent, PropType } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';

import Swal from 'sweetalert2'
import InputText from 'primevue/inputtext';
import BlockUI from 'primevue/blockui';
import ProgressSpinner from 'primevue/progressspinner';
import suiteService, { Acreditado } from 'qreventos-suite/services';
import suiteConfig from '@/services/suite.config';

export default defineComponent({
  components: {
    InputText, BlockUI, ProgressSpinner,
    Field, ErrorMessage, Form
  },
  props: {
    acreditado: {
      type: Object as PropType<Acreditado>,
      required: true
    },
    closeFunction: {
      type: Function,
      required: false
    }
  },
  data() {
    return {
      blocked: false,
      claveActual: '',
      claveNueva: '',
      claveRepetida: '',
      errorCambioClave: '',
    };
  },
  computed: {
    schema() {
      return yup.object({
        claveActual: yup.string().required(this.$t('errors.clave.actual.required')).min(3, this.$t('errors.clave.min.3')),
        claveNueva: yup.string().required(this.$t('errors.clave.nueva.required')).min(3, this.$t('errors.clave.min.3')),
        claveRepetida: yup.string().required(this.$t('errors.clave.nueva.repetir.required')).oneOf([yup.ref('claveNueva')], this.$t('errors.clave.no.coincide')),
      });
    }
  },
  methods: {
    onInvalidSubmit(values: any) {
      const errores: number = Object.values(values?.errors || {}).flat().length;
      Swal.fire({ title: "Error", html: this.$t("error.revise") + " " + errores, icon: "error"});
    },
    closeDialog(reload: boolean = false) {
      if (this.closeFunction) {
        this.closeFunction(reload);
      }
    },
    clearCambiarClave() {
      this.errorCambioClave = '';
      this.claveActual = '';
      this.claveNueva = '';
      this.claveRepetida = '';
    },   
    async aplicarCambios() {
      try {
        this.blocked = true;
        await suiteService.changePassword(suiteConfig.evento, this.acreditado.hash, this.claveActual, this.claveNueva);
        this.closeDialog(true);
      }
      catch (error) {
        this.blocked = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.blocked = false;
      }
    }, 
  },
  async mounted() {
    document.body.style.overflow = "hidden";
    this.clearCambiarClave();
  },
  unmounted() {
    document.body.style.overflow = "";
  }  
});
</script>